import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';

import TradeGroupSearchFilterView from './trade-group-search-filter-view';
import { TradeGroupSearchFilterQuery } from 'generated/graphql';
import {
  SearchProps,
  SearchResultData,
  SearchResultsProps,
} from 'semantic-ui-react';
import { useDebounce } from 'use-debounce';
import { ToastError } from 'utils';

type Props = {
  onResultSelect?: (
    event: React.MouseEvent<HTMLDivElement>,
    data: SearchResultData,
  ) => void;
  tradeGroups: TradeGroupSearchFilterQuery['searchTradeGroup'];
  fetchTradeGroups: (criteria?: string | undefined) => Promise<void>;
  defaultValue?: string;
  onDelete?: () => void;
};
const TradeGroupSearchFilterContainer: FunctionComponent<Props> = ({
  onResultSelect,
  fetchTradeGroups,
  tradeGroups,
  defaultValue,
  onDelete,
}) => {
  // State.
  const [loading, setLoading] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<SearchResultsProps>();
  const [value, setValue] = useState<string>();
  const [debouncedValue] = useDebounce(value, 600);

  if (defaultValue && value == '') {
    setValue(defaultValue);
  }

  useEffect(() => {
    const fetch = async (): Promise<void> => {
      if (debouncedValue) {
        await fetchTradeGroups(debouncedValue).catch((error) => {
          ToastError('Erreur', "Impossible de rechercher l'univers");
          setLoading(false);
          throw error;
        });
      }
    };
    fetch();
  }, [debouncedValue]);

  const onSearchChange = useCallback(
    (
      event: React.MouseEvent<HTMLElement, MouseEvent>,
      { value }: SearchProps,
    ) => {
      setValue(value);
      if (value) {
        // only when value are not empty
        // Set loading to true here to start loading before debouncing
        setLoading(true);
      }
    },
    [],
  );

  // Click on arrow
  const onClick = useCallback((): void => {
    if (onDelete) onDelete();
    setValue(undefined);
  }, []);

  useEffect(() => {
    if (tradeGroups) {
      const newSearchResults = tradeGroups.map((tradeGroup) => {
        return {
          id: tradeGroup.id,
          title: tradeGroup.name,
        };
      });
      setSearchResults(newSearchResults);
    }
    setLoading(false); // Disabling loading only when searchResults are computed
  }, [tradeGroups]);

  return (
    <TradeGroupSearchFilterView
      onClick={onClick}
      defaultValue={defaultValue}
      value={value}
      loading={loading}
      onResultSelect={onResultSelect}
      onSearchChange={onSearchChange}
      searchResults={searchResults}
    />
  );
};

export default TradeGroupSearchFilterContainer;
